import React, { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { useRouter } from "next/router";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useFormContext } from "./FormContext";
import { ENDPOINTS } from "@/config/endpoints";
import enIN from "@/locales/partner-program/enIN";
import esMX from "@/locales/partner-program/enMX";
import enUS from "@/locales/partner-program/enUS";

const schema = (t) => {
  const requiredMessage = t.FormData.requiredMessage;
  return yup.object({
    Franchise: yup
      .string()
      .required(requiredMessage)
      .notOneOf(["", null], requiredMessage),
    Broker: yup.string().required(requiredMessage),
    Factoring: yup
      .string()
      .required(requiredMessage)
      .notOneOf(["", null], requiredMessage),
    Portfolio: yup.string().required(requiredMessage),
    PortfolioInvoice: yup
      .string()
      .required(requiredMessage)
      .notOneOf(["", null], requiredMessage),
      FranchiseName: yup.string().when("Franchise", {
        is: "yes",
        then: yup.string().required(requiredMessage),
        otherwise: yup.string().notRequired(),
      }),
  });
};

const PartnerFormStep2 = () => {
  const router = useRouter();
  const { locale } = router;
  const t =
    (locale === "es-mx" && esMX) ||
    (locale === "en-us" && enUS) ||
    (locale === "en-in" && enIN);
  const validationSchema = schema(t);
  const [campaignName, setCampaignName] = useState("US");
  const [countryCode, setCountryCode] = useState("+1");
  const [ThankyouRes, setThankyouRes] = useState(false);
  const [errMSG, setErrMSG] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { state } = useFormContext();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "all",
    resolver: yupResolver(validationSchema),
  });
  const franchiseValue = watch("Franchise");

  const handleCaptchaSubmit = useCallback(
    (values) => {
      if (!executeRecaptcha) {
        return false;
      }
      executeRecaptcha("enquiryFormSubmit").then((gReCaptchaToken) => {
        onFormSubmit({ ...values, campaignName, countryCode }, gReCaptchaToken);
      });
    },
    [executeRecaptcha, campaignName, countryCode]
  );

  useEffect(() => {
    if (locale === "es-mx") {
      setCountryCode("+52");
      setCampaignName("Mexico");
    }
  }, [locale]);

  async function onFormSubmit(values, gReCaptchaToken) {
    const body = {
      formType: "PartnerProgram",
      name: state.formData.name,
      lastName: state.formData.lastName,
      email: state.formData.email,
      phoneNumber: state.formData.phone,
      company: state.formData.company,
      country: values.campaignName,
      countryCode: values.countryCode,
      franchiseName: values.FranchiseName ? values.FranchiseName : null,
      sourceUrl: window.location.href,
      formsQuestionaries: {
        Questions: {
          "How long have you been a broker or financial advisor?":
            values.Broker,
          "How many clients do you have in your portfolio? (Approximately)":
            values.Portfolio,  
        },
        Consent: {
          "Do you have any kind of franchise?": values.Franchise === 'yes' ? "Yes" : "No",
          "Have you worked with financing services such as factoring?": values.Factoring === 'yes' ? "Yes" : "No",
          "Do you have a portfolio of clients that export or invoice in USD?": values.PortfolioInvoice === 'yes' ? "Yes" : "No",
        },
      },
    };
    let config = {
      method: "post",
        url: ENDPOINTS.PARTNER_PROGRAM,
      headers: {
        "Content-Type": "application/json",
        "g-recaptcha-response": gReCaptchaToken,
      },
      data: body,
    };

    try {
      const response = await axios(config);
      if (response.status == 200) {
        if (locale === "es-mx") {
          gtag("event", "getPartnerMexico", {
            event_category: "getPartnerMexico",
            event_label: "Partner Mexico",
          });
        }
        setThankyouRes(true);
        setTimeout(() => {
          setThankyouRes(false);
          router.push("/thankyou-response");
        }, 3000);
      }
    } catch (err) {
      setErrMSG(true);
      console.log("error", err);
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    }
  }

  const getFloatingLabelClass = (fieldName) => {
    const fieldValue = watch(fieldName);
    return fieldValue || fieldValue === "yes" || fieldValue === "no"
      ? `absolute left-1 top-[-8px] text-xs text-black font-normal bg-white px-1 transition-all peer-focus:top-[-8px] peer-focus:text-xs peer-focus:text-[#555]`
      : `absolute left-1 top-2 text-sm md:text-xs text-black font-normal bg-white px-1 transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:top-[-8px] peer-focus:text-xs peer-focus:text-[#555] md:peer-placeholder-shown:text-xs`;
  };

  return (
    <div className="w-full">
      <form
        onSubmit={handleSubmit(handleCaptchaSubmit)}
      >
        <div className="mt-1">
          <label htmlFor="franchise" className="text-sm">{t.FormData.franchise}</label>
          <select
            {...register("Franchise")}
            id="franchise"
            className="peer block w-full rounded-md border border-bordercolor p-1 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none bg-white mt-xsp"
            defaultValue=""
          >
            <option value="" disabled>
              {t.FormData.selectPlaceholder}
            </option>
            <option value="yes">Si</option>
            <option value="no">No</option>
          </select>
          <p className="text-progressColor4 text-xs mt-0.5">
            {errors.Franchise?.message}
          </p>
        </div>

        {franchiseValue === "yes" && 
        <div className="mt-1">
          <label htmlFor="FranchiseName" className="text-sm">{t.FormData.franchiseName}</label>
          <input
            name="franchiseName"
            type="text"
            id="franchiseName"
            placeholder={t.FormData.franchiseName}
            className="peer block w-full rounded-md border border-bordercolor p-1 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none mt-xsp"
            {...register("FranchiseName")}
          />
          <p className="text-progressColor4 text-xs mt-0.5">
            {errors.FranchiseName?.message}
          </p>
        </div>}

        <div className="mt-1">
          <label htmlFor="broker" className="text-sm">{t.FormData.broker}</label>
          <input
            name="broker"
            type="text"
            id="broker"
            placeholder={t.FormData.broker}
            className="peer block w-full rounded-md border border-bordercolor p-1 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none mt-xsp"
            {...register("Broker")}
          />
          <p className="text-progressColor4 text-xs mt-0.5">
            {errors.Broker?.message}
          </p>
        </div>

        <div className="mt-1">
          <label htmlFor="factoring" className="text-sm">{t.FormData.factoring}</label>
          <select
            {...register("Factoring")}
            id="factoring"
            defaultValue=""
            className="peer block w-full rounded-md border border-bordercolor p-1 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none bg-white mt-xsp"
          >
            <option value="" disabled>
              {t.FormData.selectPlaceholder}
            </option>
            <option value="yes">Si</option>
            <option value="no">No</option>
          </select>
          <p className="text-progressColor4 text-xs mt-0.5">
            {errors.Factoring?.message}
          </p>
        </div>

        <div className="mt-1">
          <label htmlFor="portfolio" className="text-sm">{t.FormData.portfolio}</label>
          <input
            name="portfolio"
            type="text"
            id="portfolio"
            placeholder={t.FormData.portfolio}
            className="peer block w-full rounded-md border border-bordercolor p-1 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none mt-xsp"
            {...register("Portfolio")}
          />
          <p className="text-progressColor4 text-xs mt-0.5">
            {errors.Portfolio?.message}
          </p>
        </div>

        <div className="mt-1">
          <label htmlFor="portfolioInvoice" className="text-sm">{t.FormData.portfolioInvoice}</label>
          <select
            {...register("PortfolioInvoice")}
            id="portfolioInvoice"
            defaultValue=""
            className="peer block w-full rounded-md border border-bordercolor p-1 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none bg-white mt-xsp"
          >
            <option value="" disabled>
              {t.FormData.selectPlaceholder}
            </option>
            <option value="yes">Si</option>
            <option value="no">No</option>
          </select>
          <p className="text-progressColor4 text-xs mt-0.5">
            {errors.PortfolioInvoice?.message}
          </p>
        </div>

        <div className="mt-3">
          <button
            className="py-1 px-3 rounded-md text-white text-base2 font-normal w-full bg-gradient-to-r from-[#022B5C] to-[#0363C4] shadow-lg transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C]"
            type="submit"
          >
            {t.FormData.Submitbtn}
          </button>
        </div>
      </form>

      {ThankyouRes && (
        <div className="text-green text-center mt-4">
          <p className="text-sm font-medium">{t.FormData.ThankYou}</p>
        </div>
      )}
    </div>
  );
};

export default PartnerFormStep2;
